/**
 * CodeMirror 文件编辑器面板
 */

import React, { useCallback } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { oneDark } from '@codemirror/theme-one-dark';
import { githubLight } from '@uiw/codemirror-theme-github';
import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { php } from '@codemirror/lang-php';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { python } from '@codemirror/lang-python';
import { sql } from '@codemirror/lang-sql';
import { markdown } from '@codemirror/lang-markdown';
import { yaml } from '@codemirror/lang-yaml';
import { xml } from '@codemirror/lang-xml';
import { StreamLanguage } from '@codemirror/language';
import { shell } from '@codemirror/legacy-modes/mode/shell';
import { properties } from '@codemirror/legacy-modes/mode/properties';
import { nginx } from '@codemirror/legacy-modes/mode/nginx';
import { toml } from '@codemirror/legacy-modes/mode/toml';
import { useIsLightUi } from '@/hooks';
import { useFileEditorStore } from '@/stores';

interface FileEditorPanelProps {
  filePath: string;
  fileContent: string;
  fileSize: number;
  isDirty: boolean;
  onClose: () => void;
  onSave: (content: string) => Promise<boolean>;
}

/**
 * 根据语言标识返回对应的 CodeMirror 扩展
 */
function getLanguageExtensions(language: string) {
  switch (language) {
    case 'javascript':
    case 'typescript':
      return [javascript({ jsx: true, typescript: language === 'typescript' })];
    case 'json':
      return [json()];
    case 'php':
      return [php()];
    case 'html':
      return [html()];
    case 'css':
      return [css()];
    case 'python':
      return [python()];
    case 'sql':
      return [sql()];
    case 'markdown':
      return [markdown()];
    case 'yaml':
      return [yaml()];
    case 'xml':
      return [xml()];
    case 'shell':
      return [StreamLanguage.define(shell)];
    case 'ini':
      return [StreamLanguage.define(properties)];
    case 'toml':
      return [StreamLanguage.define(toml)];
    case 'nginx':
      return [StreamLanguage.define(nginx)];
    default:
      return [];
  }
}

/**
 * 格式化文件大小
 */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const FileEditorPanel: React.FC<FileEditorPanelProps> = ({
  filePath,
  fileContent,
  fileSize,
  isDirty,
  onClose,
  onSave,
}) => {
  const isLightUi = useIsLightUi();
  const openFile = useFileEditorStore((s) => s.openFile);
  const updateContent = useFileEditorStore((s) => s.updateContent);
  const isSaving = useFileEditorStore((s) => s.isSaving);
  const setSaving = useFileEditorStore((s) => s.setSaving);
  const setSaveError = useFileEditorStore((s) => s.setSaveError);
  const saveError = useFileEditorStore((s) => s.saveError);

  const language = openFile?.language || 'text';
  const extensions = getLanguageExtensions(language);

  const handleSave = useCallback(async () => {
    if (isSaving) return;
    setSaving(true);
    setSaveError(null);
    const content = useFileEditorStore.getState().openFile?.content || '';
    const ok = await onSave(content);
    setSaving(false);
    if (ok) {
      useFileEditorStore.getState().openFile &&
        useFileEditorStore.setState((state) => ({
          openFile: state.openFile
            ? { ...state.openFile, originalContent: content }
            : null,
        }));
    } else {
      setSaveError('保存失败，请重试');
    }
  }, [isSaving, onSave, setSaving, setSaveError]);

  const handleChange = useCallback(
    (value: string) => {
      updateContent(value);
    },
    [updateContent]
  );

  // 文件大小警告
  if (fileSize > 500_000) {
    return (
      <div className="sse-ai-editor-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ textAlign: 'center', color: isLightUi ? '#92400e' : '#f9e2af' }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>
          <div style={{ fontSize: 13 }}>文件过大（{formatSize(fileSize)}），无法在编辑器中打开</div>
        </div>
      </div>
    );
  }

  return (
    <div className="sse-ai-editor-container" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* 工具栏 */}
      <div
        className="sse-ai-editor-toolbar"
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '6px 12px',
          gap: 8,
          flexShrink: 0,
          background: isLightUi ? '#ffffff' : '#181825',
          borderBottom: `1px solid ${isLightUi ? '#e2e8f0' : '#313244'}`,
        }}
      >
        {/* 文件路径 */}
        <span
          className="sse-ai-editor-path"
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 11,
            fontFamily: "'Monaco', 'Menlo', monospace",
            color: isLightUi ? '#475569' : '#a6adc8',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={filePath}
        >
          {filePath}
        </span>

        {/* 语言标签 */}
        <span
          style={{
            fontSize: 10,
            padding: '2px 6px',
            borderRadius: 3,
            background: isLightUi ? '#e2e8f0' : '#45475a',
            color: isLightUi ? '#64748b' : '#a6adc8',
            fontWeight: 500,
            flexShrink: 0,
            textTransform: 'uppercase',
          }}
        >
          {language}
        </span>

        {/* 文件大小 */}
        <span
          style={{
            fontSize: 10,
            color: isLightUi ? '#94a3b8' : '#6c7086',
            flexShrink: 0,
          }}
        >
          {formatSize(fileSize)}
        </span>

        {/* 脏标记 */}
        {isDirty && (
          <span style={{ fontSize: 14, color: isLightUi ? '#dc2626' : '#f38ba8', flexShrink: 0 }} title="未保存">
            ●
          </span>
        )}

        {/* 保存按钮 */}
        <button
          type="button"
          className="sse-ai-editor-save-btn"
          onClick={handleSave}
          disabled={isSaving || !isDirty}
          style={{
            padding: '4px 10px',
            border: 'none',
            borderRadius: 4,
            background: isDirty ? (isLightUi ? '#4f46e5' : '#667eea') : (isLightUi ? '#e2e8f0' : '#45475a'),
            color: '#fff',
            fontSize: 11,
            fontWeight: 500,
            cursor: isDirty && !isSaving ? 'pointer' : 'not-allowed',
            opacity: isDirty && !isSaving ? 1 : 0.5,
            transition: 'all 0.15s',
            flexShrink: 0,
          }}
        >
          {isSaving ? '保存中...' : '保存'}
        </button>

        {/* 关闭按钮 */}
        <button
          type="button"
          className="sse-ai-editor-close"
          onClick={onClose}
          style={{
            width: 26,
            height: 26,
            border: 'none',
            background: 'transparent',
            borderRadius: 4,
            color: isLightUi ? '#64748b' : '#6c7086',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'background 0.15s, color 0.15s',
            fontSize: 16,
          }}
          title="关闭编辑器"
        >
          ×
        </button>
      </div>

      {/* 保存错误提示 */}
      {saveError && (
        <div
          style={{
            padding: '4px 12px',
            fontSize: 11,
            background: isLightUi ? 'rgba(239, 68, 68, 0.1)' : 'rgba(243, 139, 168, 0.1)',
            color: isLightUi ? '#dc2626' : '#f38ba8',
            borderBottom: `1px solid ${isLightUi ? 'rgba(239, 68, 68, 0.2)' : 'rgba(243, 139, 168, 0.2)'}`,
            flexShrink: 0,
          }}
        >
          {saveError}
          <button
            onClick={() => setSaveError(null)}
            style={{ marginLeft: 8, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}
          >
            关闭
          </button>
        </div>
      )}

      {/* 文件大小警告 */}
      {fileSize > 100_000 && (
        <div
          className="sse-ai-editor-size-warning"
          style={{
            padding: '4px 12px',
            fontSize: 10,
            background: isLightUi ? 'rgba(251, 191, 36, 0.1)' : 'rgba(249, 226, 175, 0.1)',
            color: isLightUi ? '#92400e' : '#f9e2af',
            borderBottom: `1px solid ${isLightUi ? 'rgba(251, 191, 36, 0.2)' : 'rgba(249, 226, 175, 0.2)'}`,
            flexShrink: 0,
          }}
        >
          大文件 ({formatSize(fileSize)})，编辑器性能可能受影响
        </div>
      )}

      {/* CodeMirror 编辑器 */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <CodeMirror
          value={fileContent}
          onChange={handleChange}
          theme={isLightUi ? githubLight : oneDark}
          extensions={extensions}
          basicSetup={{
            lineNumbers: true,
            highlightActiveLineGutter: true,
            highlightSpecialChars: true,
            foldGutter: true,
            drawSelection: true,
            dropCursor: true,
            allowMultipleSelections: true,
            indentOnInput: true,
            syntaxHighlighting: true,
            bracketMatching: true,
            closeBrackets: true,
            autocompletion: true,
            rectangularSelection: true,
            crosshairCursor: true,
            highlightActiveLine: true,
            closeBracketsKeymap: true,
            defaultKeymap: true,
            searchKeymap: true,
            historyKeymap: true,
            foldKeymap: true,
            completionKeymap: true,
            lintKeymap: true,
          }}
        />
      </div>
    </div>
  );
};
